import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { ApiSettings } from '../config/api-settings';
import { ApiKey, ApiKeyStatus } from '../models';

export interface RollResult {
  apiKey: ApiKey;
  revoked: ApiKey;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class ApiKeysService {
  constructor(private http: HttpClient) {}

  private activeKeysCache$?: Observable<ApiKey[]>;

  getApiKeys(status?: ApiKeyStatus): Observable<ApiKey[]> {
    const URL = `${ApiSettings.fullPath}api_keys`;
    const params = status ? new HttpParams().set('status', status) : undefined;

    return this.http.get<any>(URL, { params }).pipe(
      map(res => (res.api_keys ?? []).map((key: object) => new ApiKey(key))),
    );
  }

  getActiveApiKeys(): Observable<ApiKey[]> {
    if (!this.activeKeysCache$) {
      this.activeKeysCache$ = this.getApiKeys('active').pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.activeKeysCache$;
  }

  private invalidateActiveKeysCache(): void {
    this.activeKeysCache$ = undefined;
  }

  createApiKey(args: { name: string }): Observable<ApiKey> {
    const URL = `${ApiSettings.fullPath}api_keys`;

    return this.http.post<any>(URL, { api_key: { name: args.name } }).pipe(
      map(res => new ApiKey(res.api_key)),
      tap(() => this.invalidateActiveKeysCache()),
    );
  }

  revealApiKey(id: string): Observable<string> {
    const URL = `${ApiSettings.fullPath}api_keys/${id}/reveal`;

    return this.http.get<any>(URL).pipe(
      map(res => res.api_key?.key ?? ''),
    );
  }

  revokeApiKey(id: string): Observable<ApiKey> {
    const URL = `${ApiSettings.fullPath}api_keys/${id}`;

    return this.http.delete<any>(URL).pipe(
      map(res => new ApiKey(res.api_key)),
      tap(() => this.invalidateActiveKeysCache()),
    );
  }

  deleteApiKey(id: string): Observable<void> {
    const URL = `${ApiSettings.fullPath}api_keys/${id}`;

    return this.http.delete<any>(URL).pipe(
      map(() => undefined),
      tap(() => this.invalidateActiveKeysCache()),
    );
  }

  rollApiKey(id: string): Observable<RollResult> {
    const URL = `${ApiSettings.fullPath}api_keys/${id}/roll`;

    return this.http.post<any>(URL, {}).pipe(
      map(res => ({
        apiKey: new ApiKey(res.api_key),
        revoked: new ApiKey(res.revoked),
        message: res.message,
      })),
      tap(() => this.invalidateActiveKeysCache()),
    );
  }
}
